<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import { VueFinalModal } from 'vue-final-modal';
  import { ColoredButton, FormInput, ThemeProvider, UiModalHeader } from '@/shared/ui';

  const props = defineProps<{
    title?: string;
    initialName?: string;
    nameError?: string;
    isSaving?: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'save', data: { name: string }): void;
  }>();

  const name = ref(props.initialName ?? '');
  const localNameError = ref('');

  const isFormValid = computed(() => name.value.trim().length > 0);

  watch(
    () => props.initialName,
    (nextInitialName) => {
      name.value = nextInitialName ?? '';
    },
    { immediate: true }
  );

  watch(
    () => props.nameError,
    (nextNameError) => {
      localNameError.value = nextNameError ?? '';
    },
    { immediate: true }
  );

  const handleClose = () => {
    emit('close');
  };

  const handleNameUpdate = (value: string) => {
    name.value = value;
    localNameError.value = '';
  };

  const handleSave = () => {
    if (!isFormValid.value || props.isSaving) {
      return;
    }

    emit('save', {
      name: name.value.trim(),
    });
  };

  const handleClosed = () => {
    name.value = props.initialName ?? '';
    localNameError.value = '';
  };
</script>

<template>
  <VueFinalModal
    class="flex justify-center items-center"
    content-class="flex flex-col bg-white-01 rounded-2xl w-[500px] shadow-lg"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
    @closed="handleClosed"
  >
    <ThemeProvider class="flex flex-col">
      <div
        data-testid="edit-physical-location-modal"
        class="flex flex-col"
      >
        <UiModalHeader
          class="px-6 py-4 text-sm border-b border-gray-02 shrink-0"
          :title="title ?? 'Editar local'"
          @close="handleClose"
        />

        <div class="px-6 py-6 space-y-4">
          <FormInput
            id="edit-physical-location-name"
            :model-value="name"
            label="Local:"
            type="text"
            placeholder="Informe o nome do Local"
            label-class="text-sm text-gray-07 font-semibold"
            :max-length="100"
            :error="localNameError"
            @update:model-value="handleNameUpdate"
          />
        </div>

        <div class="px-6 py-8 flex justify-end gap-3 border-t border-gray-02 shrink-0">
          <ColoredButton
            data-testid="edit-physical-location-cancel"
            text="Cancelar"
            outlined
            color="gray-03"
            @click="handleClose"
          />
          <ColoredButton
            data-testid="edit-physical-location-save"
            :disabled="!isFormValid || isSaving"
            text="Salvar"
            @click="handleSave"
          />
        </div>
      </div>
    </ThemeProvider>
  </VueFinalModal>
</template>

<style scoped>
  .vfm__overlay {
    background-color: rgba(0, 0, 0, 0.5);
  }
</style>
